import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useRuntimeConfig } from '@/config/useRuntimeConfig';
import { useAuth } from '@/auth/useAuth';
import { useCan } from '@/auth/consoleCapabilityContext';
import { useActingOrg } from '@/auth/actingOrgContext';
import {
    fetchAdminApps,
    fetchAppRoles,
    fetchRoleKeys,
    fetchRoleMaps,
    fetchRoleMapScopeDiff,
    fetchRoleMapSyncStatus,
} from '@/api/console';
import { fetchOrgLevels } from '@/api/orgAdmin';
import { useAccountAssignments } from './useAccountCounts';
import { useRoleMapView } from './useRoleMapView';
import { useRoleMapWrites } from './useRoleMapWrites';
import type { RoleMapFilterMenu } from './RoleMapFilterButton';
import { accountCountsByRoleKey, computeScopeOverrideSummaries, hasActiveFilters, type RoleMapFilterState } from './helpers';
import {
    DEFAULT_VISIBLE_COLUMNS,
    ROLE_MAP_COLUMNS,
    standardRolesFromRoleKeys,
    type RoleMapColumnKey,
    type RoleMapGroup,
    type RoleMapScope,
    type RoleMapSortDirection,
    type RoleMapSortField,
    type RoleMapStatusFilter,
} from './types';

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/**
 * EAA-1837: view model behind `/console/role-map` — the "Liên kết vai trò"
 * table (design export `role-map-table.html`). Everything stateful lives here
 * or in the two hooks it composes (`useRoleMapView` = pure derivation,
 * `useRoleMapWrites` = every mutation), so `RoleMapPage.tsx` stays a layout
 * shell. The three-way split mirrors `accounts/useAccountsScan` +
 * `useAccountsView`, the app's other bulk-select table.
 *
 * Two things carried over unchanged from the EAA-1817 coverage board because
 * they were, and remain, correct: the capability model
 * (`console.rolemap.view` gates the route, `console.rolemap.manage` means "may
 * edit the Mặc định scope", `org:{id}.manage` gates the org's own override) and
 * the acting-org account-count walk (`useAccountCounts`).
 *
 * ONE HARD BACKEND CONSTRAINT shapes this whole screen. Every EAA-1837 bulk
 * endpoint is `/organizations/{org_id}/role-map/...` and reads through
 * `RoleMapService.list_for_organization`, which deliberately EXCLUDES
 * ecosystem-wide defaults (`organization_id IS NULL`); `bulk_set_scope` and
 * `bulk_deactivate` additionally filter `organization_id=org_id`. There is
 * therefore NO way to bulk-scope / bulk-resync / bulk-delete / export a default
 * row — such ids come straight back in `skipped`. So while the "Phạm vi"
 * dropdown sits on "Mặc định", the bulk bar, the row checkboxes and the CSV
 * export are all disabled with an explicit reason rather than firing requests
 * that are guaranteed to no-op. See `bulkDisabledReason` below.
 */
export function useRoleMapTable({ active = true }: { active?: boolean } = {}) {
    // EAA-1845: `useTranslation` is a hook, not a component — calling it here
    // is fine even though this file has no JSX. `bulkDisabledReason` below is
    // read straight into the DOM by `RoleMapListView`, so it is user-facing
    // text like anything else in this batch.
    const { t } = useTranslation();
    const can = useCan();
    const config = useRuntimeConfig();
    const { authenticated, accessToken } = useAuth();
    const acting = useActingOrg();

    const actingOrgId = acting?.actingOrgId ?? null;
    const actingOrgName = acting?.actingOrg?.name ?? null;
    // EAA-1841: `active` is false while the merged screen is showing the board
    // view. Hooks cannot be called conditionally, so gating the QUERIES is how
    // the inactive view stops costing requests. The keys this shares with the
    // board (`admin-apps`, `role-maps`, `role-keys`) are identical, so whichever
    // view is active fetches them once and the other reads the same cache.
    const enabled = active && authenticated && Boolean(accessToken) && can('console.rolemap.view');
    const canManageDefault = enabled && can('console.rolemap.manage');
    const canManageOrg = enabled && Boolean(actingOrgId) && can(`org:${actingOrgId}.manage`);
    const canSync = enabled && can('console.rolemap.sync');

    /* ---------------- queries ---------------- */
    const appsQuery = useQuery({
        queryKey: ['admin-apps', accessToken],
        queryFn: () => fetchAdminApps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const roleMapsQuery = useQuery({
        queryKey: ['role-maps', accessToken],
        queryFn: () => fetchRoleMaps(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const syncStatusQuery = useQuery({
        queryKey: ['role-map-sync-status', accessToken],
        queryFn: () => fetchRoleMapSyncStatus(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const levelsQuery = useQuery({
        queryKey: ['org-levels', accessToken, actingOrgId],
        queryFn: () => fetchOrgLevels(config.apiBase, accessToken as string, actingOrgId as string),
        enabled: enabled && Boolean(actingOrgId),
        retry: false,
    });
    // EAA-1817: the governed role-key vocabulary (`GET /api/v1/role-keys`) —
    // replaces the old Levels-only derivation for `standardRoles` below. See
    // `standardRolesFromRoleKeys`'s docstring for why the source changed and
    // what it falls back to when this comes back empty.
    const roleKeysQuery = useQuery({
        queryKey: ['role-keys', accessToken],
        queryFn: () => fetchRoleKeys(config.apiBase, accessToken as string),
        enabled,
        retry: false,
    });
    const assignmentsQuery = useAccountAssignments(
        config.apiBase,
        accessToken,
        actingOrgId,
        enabled
    );

    const apps = useMemo(() => appsQuery.data ?? [], [appsQuery.data]);
    const rows = useMemo(() => roleMapsQuery.data ?? [], [roleMapsQuery.data]);
    // EAA-1817 (frame 56): org-scope +/- diff summary derived from the same
    // cached role-map payload — no whole-list scope-diff endpoint exists.
    const scopeOverrideSummaries = useMemo(
        () => computeScopeOverrideSummaries(rows, actingOrgId, apps),
        [rows, actingOrgId, apps]
    );
    const levels = useMemo(() => levelsQuery.data?.results ?? [], [levelsQuery.data]);
    const standardRoles = useMemo(
        () => standardRolesFromRoleKeys(roleKeysQuery.data ?? [], rows),
        [roleKeysQuery.data, rows]
    );
    const accountCounts = useMemo(
        () => accountCountsByRoleKey(assignmentsQuery.data ?? [], levels),
        [assignmentsQuery.data, levels]
    );

    /* ---------------- scope ---------------- */
    const [scope, setScopeState] = useState<RoleMapScope>(canManageDefault ? 'default' : 'org');
    useEffect(() => {
        // EAA-1817, carried over: an operator with no `console.rolemap.manage`
        // never gets the Mặc định slice — force back to 'org' if capabilities
        // change under them (e.g. an acting-org switch while mounted).
        if (!canManageDefault && scope === 'default') setScopeState('org');
    }, [canManageDefault, scope]);

    /* ---------------- filters / sort / paging / columns ---------------- */
    const [search, setSearchState] = useState('');
    const [statusFilter, setStatusFilter] = useState<RoleMapStatusFilter[]>([]);
    const [appFilter, setAppFilter] = useState<string[]>([]);
    const [sortField, setSortField] = useState<RoleMapSortField>('role_key');
    const [sortDirection, setSortDirection] = useState<RoleMapSortDirection>('asc');
    const [page, setPage] = useState(1);
    const [pageSize, setPageSizeState] = useState(PAGE_SIZE_OPTIONS[0]);
    const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
    const [visibleColumns, setVisibleColumns] =
        useState<RoleMapColumnKey[]>(DEFAULT_VISIBLE_COLUMNS);
    const searchInputRef = useRef<HTMLInputElement>(null);
    // EAA-1837: which toolbar menu is open lives here, not inside the toolbar,
    // because the design's column-header funnel icons ("Lọc theo ứng dụng",
    // "Lọc theo tình trạng") open the very same menus — they are a second
    // trigger for one filter, not a separate filtering mechanism.
    const [openFilter, setOpenFilter] = useState<RoleMapFilterMenu | null>(null);

    const filters: RoleMapFilterState = useMemo(
        () => ({ search, status: statusFilter, apps: appFilter }),
        [search, statusFilter, appFilter]
    );
    const filtersActive = hasActiveFilters(filters);

    const view = useRoleMapView(
        rows,
        apps,
        accountCounts,
        scope,
        actingOrgId,
        filters,
        sortField,
        sortDirection,
        page,
        pageSize
    );

    /* ---------------- selection (per VISUAL row = per group) ---------------- */
    // EAA-1837: the checkbox selects a GROUP KEY, and every bulk call expands the
    // selected keys back into EVERY underlying RoleMap id. Selecting the
    // `teacher_lead`/LMS row therefore acts on both `teacher` and `admin_class`
    // — see `RoleMapGroup`'s docstring for the EAA-1828 bug class this avoids.
    const { visibleGroups, pageGroups } = view;
    const selectedGroups = useMemo(
        () => visibleGroups.filter((group) => selectedKeys.has(group.key)),
        [visibleGroups, selectedKeys]
    );
    const selectedIds = useMemo(
        () => selectedGroups.flatMap((group) => group.ids),
        [selectedGroups]
    );

    // Prune the selection whenever the visible set changes (a bulk delete removes
    // rows; a filter change hides them) so "Đã chọn N dòng" can never count a row
    // the operator can no longer see or act on.
    useEffect(() => {
        setSelectedKeys((prev) => {
            if (prev.size === 0) return prev;
            const visible = new Set(visibleGroups.map((group) => group.key));
            let changed = false;
            const next = new Set<string>();
            for (const key of prev) {
                if (visible.has(key)) next.add(key);
                else changed = true;
            }
            return changed ? next : prev;
        });
    }, [visibleGroups]);

    const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);

    /**
     * EAA-1884: replaces the old `toggleRow`/`toggleAll`/`allSelected`/
     * `someSelected`/`pageKeys` quintet now that `RoleMapTable` wires the
     * kit `DataTable`'s native `rowSelection` (antd `Table` underneath)
     * instead of hand-rolled checkboxes. antd's own selection hook is
     * CONTROLLED the same way this Set already was: it calls back with the
     * full next key list (previous `selectedRowKeys` plus/minus whatever the
     * operator just toggled on the CURRENTLY RENDERED page), and — because
     * every disabled row is excluded from its own "select all" bookkeeping —
     * reproduces `allSelected`/`someSelected`'s exact page-scoped math and
     * the header checkbox's auto-disable-when-every-row-disabled behaviour
     * for free (verified against antd's `useSelection` hook). No page-only
     * derivation is needed here any more; antd already has it.
     */
    const onSelectionChange = useCallback((keys: (string | number)[]) => {
        setSelectedKeys(new Set(keys.map(String)));
    }, []);

    /* ---------------- toolbar setters (each resets paging) ---------------- */
    const setSearch = useCallback((value: string) => {
        setSearchState(value);
        setPage(1);
    }, []);

    const toggleStatus = useCallback((value: RoleMapStatusFilter) => {
        setStatusFilter((prev) =>
            prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
        );
        setPage(1);
    }, []);

    const toggleApp = useCallback((code: string) => {
        setAppFilter((prev) =>
            prev.includes(code) ? prev.filter((v) => v !== code) : [...prev, code]
        );
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setSearchState('');
        setStatusFilter([]);
        setAppFilter([]);
        setPage(1);
    }, []);

    const setScope = useCallback(
        (next: RoleMapScope) => {
            setScopeState(next);
            setPage(1);
            // A selection made in one scope is meaningless in the other (the two
            // slices share no rows), and carrying it over would let a Mặc định
            // selection leak into an org-scoped bulk call.
            clearSelection();
        },
        [clearSelection]
    );

    const setPageSize = useCallback((size: number) => {
        setPageSizeState(size);
        setPage(1);
    }, []);

    const toggleSort = useCallback(
        (field: RoleMapSortField) => {
            if (field === sortField) {
                setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                return;
            }
            setSortField(field);
            setSortDirection('asc');
        },
        [sortField]
    );

    const focusSearch = useCallback(() => searchInputRef.current?.focus(), []);

    /* ---------------- column visibility ---------------- */
    const isColumnVisible = useCallback(
        (key: RoleMapColumnKey) => visibleColumns.includes(key),
        [visibleColumns]
    );
    const toggleColumn = useCallback((key: RoleMapColumnKey) => {
        const spec = ROLE_MAP_COLUMNS.find((column) => column.key === key);
        if (spec?.pinned) return;
        setVisibleColumns((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
        );
    }, []);
    const resetColumns = useCallback(() => setVisibleColumns(DEFAULT_VISIBLE_COLUMNS), []);

    /* ---------------- bulk gating ---------------- */
    const bulkEnabled = scope === 'org' && Boolean(actingOrgId) && canManageOrg;
    const canManageCurrentScope = scope === 'default' ? canManageDefault : canManageOrg;

    /**
     * EAA-1837: why a bulk action is unavailable, in the operator's words; `null`
     * when it IS available. The Mặc định case is a real backend limitation stated
     * plainly rather than papered over — see this hook's docstring.
     */
    let bulkDisabledReason: string | null = null;
    if (scope === 'default') {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.scopeDefault');
    } else if (!actingOrgId) {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.noOrg');
    } else if (!canManageOrg) {
        bulkDisabledReason = t('console.roleMap.bulkDisabledReason.noPermission');
    }

    /* ---------------- writes ---------------- */
    const writes = useRoleMapWrites({
        apiBase: config.apiBase,
        token: accessToken,
        actingOrgId,
        scope,
        canManageDefault,
        canManageOrg,
        clearSelection,
    });

    const applyBulkScope = useCallback(
        (target: 'default' | 'org') => {
            if (!bulkEnabled || !actingOrgId || selectedIds.length === 0) return;
            writes.setScopeFor({
                ids: selectedIds,
                organization_id: target === 'default' ? null : actingOrgId,
            });
        },
        [bulkEnabled, actingOrgId, selectedIds, writes]
    );

    const applyBulkResync = useCallback(() => {
        if (!bulkEnabled || selectedIds.length === 0) return;
        writes.resyncIds(selectedIds);
    }, [bulkEnabled, selectedIds, writes]);

    const applyBulkDelete = useCallback(() => {
        if (!bulkEnabled || selectedIds.length === 0) return;
        writes.deleteIds(selectedIds);
    }, [bulkEnabled, selectedIds, writes]);

    const resyncGroup = useCallback(
        (group: RoleMapGroup) => {
            if (!bulkEnabled) return;
            writes.resyncIds(group.ids);
        },
        [bulkEnabled, writes]
    );

    const setGroupScope = useCallback(
        (group: RoleMapGroup, target: 'default' | 'org') => {
            if (!bulkEnabled || !actingOrgId) return;
            writes.setScopeFor({
                ids: group.ids,
                organization_id: target === 'default' ? null : actingOrgId,
            });
        },
        [bulkEnabled, actingOrgId, writes]
    );

    /** EAA-1837: the export endpoint runs through the same org-scoped
     *  `list_for_organization` queryset as the bulk actions, so it can only
     *  export the acting org's own override rows — disabled in the Mặc định
     *  slice for exactly that reason, never silently exporting the wrong set. */
    const exportEnabled = scope === 'org' && Boolean(actingOrgId);
    const exportCsv = useCallback(() => {
        if (!exportEnabled) return Promise.resolve();
        return writes.exportCsv(selectedIds);
    }, [exportEnabled, writes, selectedIds]);

    /* ---------------- re-point ("Đổi vai trò") modal ---------------- */
    // EAA-1817: `PATCH /role-maps/{id}` is IsSuperAdmin-only server-side (see
    // `useRoleMapWrites.repointMutation`'s docstring) — restrict the trigger to
    // single-id groups only (an unambiguous target) and let the modal itself
    // gate visibility on `canManageDefault`.
    const [repointTarget, setRepointTarget] = useState<RoleMapGroup | null>(null);
    const openRepoint = useCallback(
        (group: RoleMapGroup) => {
            writes.repointMutation.reset();
            setRepointTarget(group);
        },
        [writes]
    );
    const closeRepoint = useCallback(() => setRepointTarget(null), []);

    // Same catalog `fetchAppRoles` the Add-link picker uses — a re-point can
    // only land on a role the target application currently declares.
    const repointAppRolesQuery = useQuery({
        queryKey: ['admin-app-roles', repointTarget?.application, accessToken],
        queryFn: () =>
            fetchAppRoles(config.apiBase, accessToken as string, repointTarget!.application),
        enabled: enabled && Boolean(repointTarget) && canManageDefault,
        retry: false,
    });

    /* ---------------- scope-diff ("Xem khác biệt với mặc định") panel ---------------- */
    // EAA-1817: per-row diff against the ecosystem default (frame 46), scoped
    // to whichever group's row action opened it — not the whole-list summary
    // the design export shows, which has no single-pair endpoint behind it
    // (`fetchRoleMapScopeDiff` takes exactly one (application, role_key) pair).
    const [scopeDiffTarget, setScopeDiffTarget] = useState<RoleMapGroup | null>(null);
    const openScopeDiff = useCallback((group: RoleMapGroup) => setScopeDiffTarget(group), []);
    const closeScopeDiff = useCallback(() => setScopeDiffTarget(null), []);

    const scopeDiffQuery = useQuery({
        queryKey: [
            'role-map-scope-diff',
            accessToken,
            scopeDiffTarget?.application,
            scopeDiffTarget?.role_key,
            actingOrgId,
        ],
        queryFn: () =>
            fetchRoleMapScopeDiff(config.apiBase, accessToken as string, {
                application: scopeDiffTarget!.application,
                roleKey: scopeDiffTarget!.role_key,
                organization: actingOrgId as string,
            }),
        enabled: enabled && Boolean(scopeDiffTarget) && Boolean(actingOrgId) && scope === 'org',
        retry: false,
    });

    const dropOverride = useCallback(() => {
        if (!scopeDiffTarget || !actingOrgId) return;
        writes.dropOverrideMutation.mutate(
            {
                application: scopeDiffTarget.application,
                roleKey: scopeDiffTarget.role_key,
                organization: actingOrgId,
            },
            { onSuccess: () => setScopeDiffTarget(null) }
        );
    }, [scopeDiffTarget, actingOrgId, writes]);

    /* ---------------- add-link modal ---------------- */
    const [addOpen, setAddOpen] = useState(false);
    const [addApp, setAddApp] = useState<string | null>(null);
    const openAdd = useCallback(() => setAddOpen(true), []);
    const closeAdd = useCallback(() => setAddOpen(false), []);

    /**
     * EAA-1841: the picker now asks for the acting org's OWN catalog rows too
     * when the modal is writing an org-scoped link. Two reasons, both concrete:
     *
     * `GET /admin/app-roles` defaults to ecosystem-wide rows only
     * (`organization__isnull=True` server-side) and `organization={uuid}` is
     * what adds an org's custom rows — the param exists precisely so an org
     * override can link them. And every role created through
     * `POST /admin/app-roles` is upserted ORG-SCOPED, so without this the
     * operator would create a role here and watch it never appear in the list
     * they created it for.
     *
     * Omitted in the default scope on purpose: a default-scope link must not be
     * able to point at a role that exists in one org only.
     */
    const addAppRolesOrg = scope === 'org' ? actingOrgId : null;
    const addAppRolesQuery = useQuery({
        queryKey: ['admin-app-roles', addApp, addAppRolesOrg, accessToken],
        queryFn: () =>
            fetchAppRoles(config.apiBase, accessToken as string, addApp as string, {
                ...(addAppRolesOrg ? { organization: addAppRolesOrg } : {}),
            }),
        enabled: enabled && addOpen && Boolean(addApp),
        retry: false,
    });

    /**
     * EAA-1841: the app a new role is being created in from inside the Add-link
     * modal — the escape hatch for the dead-end this flow used to have, where a
     * missing role meant leaving the console entirely. `null` keeps
     * `CreateAppRoleModal` closed.
     */
    const [roleCreateApp, setRoleCreateApp] = useState<string | null>(null);
    const openRoleCreate = useCallback((code: string) => setRoleCreateApp(code), []);
    const closeRoleCreate = useCallback(() => setRoleCreateApp(null), []);

    const { createLinks: runCreate } = writes;
    const createLinks = useCallback(
        async (input: { role_key: string; application: string; appRoles: string[] }) => {
            await runCreate(input);
            setAddOpen(false);
        },
        [runCreate]
    );

    const loading =
        roleMapsQuery.isLoading ||
        appsQuery.isLoading ||
        roleKeysQuery.isLoading ||
        (Boolean(actingOrgId) && levelsQuery.isLoading);

    return {
        enabled,
        /** EAA-1841: the capability gate ALONE, unaffected by `active` — the
         *  merged page decides whether to render the route from this, while
         *  `enabled` additionally means "this view is the one on screen". */
        canView: authenticated && Boolean(accessToken) && can('console.rolemap.view'),
        loading,
        isError: roleMapsQuery.isError || appsQuery.isError,

        canManageDefault,
        canManageOrg,
        canManageCurrentScope,
        canSync,
        actingOrgId,
        actingOrgName,

        apps,
        standardRoles,
        syncStatusQuery,
        syncMutation: writes.syncMutation,

        totalLinks: view.scopedRows.length,
        appCount: view.appCount,

        search,
        setSearch,
        searchInputRef,
        focusSearch,
        statusFilter,
        toggleStatus,
        appFilter,
        toggleApp,
        clearFilters,
        filtersActive,
        openFilter,
        setOpenFilter,

        scope,
        setScope,
        scopeOverrideSummaries,

        visibleColumns,
        isColumnVisible,
        toggleColumn,
        resetColumns,

        pageGroups,
        filteredCount: visibleGroups.length,
        sortField,
        sortDirection,
        toggleSort,

        selectedKeys,
        selectedGroups,
        selectedCount: selectedGroups.length,
        selectedIdCount: selectedIds.length,
        onSelectionChange,
        clearSelection,

        bulkEnabled,
        bulkDisabledReason,
        bulkBusy: writes.busy,
        applyBulkScope,
        applyBulkResync,
        applyBulkDelete,
        removeGroup: writes.removeGroup,
        resyncGroup,
        setGroupScope,

        exportCsv,
        exportBusy: writes.exportBusy,
        exportEnabled,

        repointTarget,
        openRepoint,
        closeRepoint,
        repointMutation: writes.repointMutation,
        repointAppRoles: repointAppRolesQuery.data ?? [],
        repointAppRolesLoading: repointAppRolesQuery.isLoading,

        scopeDiffTarget,
        openScopeDiff,
        closeScopeDiff,
        scopeDiffQuery,
        dropOverride,
        dropOverrideBusy: writes.dropOverrideMutation.isPending,

        undo: writes.undo,

        addOpen,
        openAdd,
        closeAdd,
        addApp,
        setAddApp,
        addAppRoles: addAppRolesQuery.data ?? [],
        addAppRolesLoading: addAppRolesQuery.isLoading,
        addAppRolesOrg,
        roleCreateApp,
        openRoleCreate,
        closeRoleCreate,
        createLinks,
        addBusy: writes.createBusy,
        createMutation: writes.createMutation,

        page: view.page,
        setPage,
        pageSize,
        setPageSize,
        pageSizeOptions: PAGE_SIZE_OPTIONS,
        totalPages: view.totalPages,
    };
}

export type RoleMapTableVM = ReturnType<typeof useRoleMapTable>;
